// The `list.outreach` namespace — the Outreach step of the list wizard (PREC-3687). Kept in its own
// file so the (over-length) main translation file stays within the architecture-gate line ratchet.
// Spread back in as `list.outreach` in en.ts.
import { enCampaignRouting } from "./enCampaignRouting";

export const enOutreach = {
	routing: enCampaignRouting,
	subHeader: "Campaigns send one sequence to many leads. Chat writes one message per signal.",
	howOutreachWorks: "How outreach works",
	skip: {
		title: "This step is optional",
		description:
			"If you only want to enrich this list for now, skip ahead — you can set up outreach from List Settings whenever you're ready.",
		action: "Skip to settings",
		dismiss: "Dismiss",
	},
	general: {
		title: "General",
		subtitle: "Everything both send paths read.",
		instruction: {
			label: "Copywriting instructions",
			optional: "Optional",
			help: "The input for the copywriting agents to consider when drafting messages — the reason you are reaching out, and what the agent should make of it. Not the tone or the structure, which come from the framework and the sender's style profile. Chat drafts from this directly, once per signal; campaigns read it wherever a step contains an AI snippet. Leave it empty and the agents work from the signal alone.",
			placeholder: "Why are you reaching out to this list, and what should the agent make of it?",
			variables: "Variables",
			templates: "Templates",
			searchVariables: "Search variables…",
			searchTemplates: "Search templates…",
			savedTemplates: "Saved templates",
			dismissPrefill: "Remove this suggestion",
			suggestedTemplates: "Suggested for this list type",
			defaultBadge: "Default",
			loading: "Loading…",
			noTemplates: "No saved templates for this list type yet.",
			noMatches: "No matches.",
			loaded: "loaded",
			updateTemplate: "Update current template",
			saveAsNew: "Save as new template",
			clear: "Clear",
			// Names of the built-in suggestions (`SUGGESTED_COPYWRITING_INSTRUCTIONS` in
			// @signalist/shared-types, keyed by `nameKey`). Only the name is translated — the body is
			// guidance for the agent, and the language a message is written in comes from the list's
			// own language setting.
			suggestions: {
				latestAccountSignal: "Latest account signal",
				newCompanyFollower: "New company follower",
				profileVisitor: "Profile visitor",
				postEngagement: "Post engagement",
				newInRole: "New in role",
				uploadedList: "Uploaded list",
				monitoredCompanyNews: "Monitored company news",
			},
			// Mirrors the campaign editor's variable set — the instruction offers exactly the same
			// tokens, so a group here is only a way of reading one long flat list.
			groups: {
				lead: "Lead",
				company: "Company",
				event: "Event",
				column: "Uploaded columns",
				sender: "Sender",
			},
		},
		saveTemplate: {
			title: "Save as new template",
			scopeHint: "Saved templates are available on every list of this type.",
			nameLabel: "Template name",
			namePlaceholder: "e.g. Funding round — DACH",
			makeDefault: "Make this the default for new lists",
			save: "Save template",
		},
	},
	campaigns: {
		title: "Campaigns",
		subtitle: "One sequence, many leads.",
		createNew: "Create new campaign",
		copyFromList: "Copy from other list",
		applyEventEndDate: "Apply event end date",
		viewAnalytics: "View detailed analytics",
		enable: {
			label: "Enable campaigns on this list",
			description:
				"Off hides the campaign table here and the campaign options in the Reach out menu. Existing campaigns keep their data and come back untouched when you switch it on again.",
		},
		disableBlocked: {
			title: "Pause these campaigns before turning campaigns off",
			description:
				"These campaigns are still sending. Leads already enrolled keep going, so pause them first — then campaigns can be switched off.",
			gotIt: "Got it",
		},
	},
	chat: {
		title: "Chat",
		subtitle: "One message written for one lead, from the specific signal that surfaced them.",
		autoCreate: {
			label: "Always create AI copy for qualified leads and signals",
			description: "Drafts land in the lead's chat view as soon as a signal card is finalised.",
		},
		recommendedForTracker: "Recommended for Profile Tracker",
		credit: {
			perLead: "1 credit per lead",
			included: "Included / No additional credits per lead",
		},
		language: {
			label: "Language",
			description: "Auto follows the lead, bounded by what the sending user can read.",
			tooltip: {
				p1: "Auto writes in the lead's own language, but only when the resolved sender lists it as one they speak.",
				p2: "Otherwise it falls back to that sender's primary language — so an Italian lead written to by a German-and-English sender gets English, not Italian.",
				p3: "Each sender's languages come from their own profile settings.",
			},
		},
		framework: {
			label: "Copywriting framework",
			auto: "Auto-suggest",
			description: "Auto lets the Framework Strategy Agent choose a shape per signal.",
			tooltip: {
				p1: "The shape of the message — what it opens with and how it earns the ask.",
				p2: "Auto-suggest lets the Framework Strategy Agent pick per signal, so a funding round gets a Trigger opener while a lead with no strong signal gets a Soft Starter.",
				p3: "Override to force one shape across every message on this list.",
			},
		},
		senders: {
			label: "Allowed senders",
			description:
				"The pool chat draws from. A sender can only be picked for a channel they have connected.",
		},
		senderRouting: {
			title: "How senders get picked if multiple senders are activated",
			summary: "Warm connections win, then language fit, then whoever has the lightest queue.",
			openHint:
				"Describe in plain language which sender should write, and when. The agent decides per lead.",
			placeholder: "Which sender should write, and when?",
			scopeHintCompanyWide: "Saved sender rules are available on every list in the company.",
		},
	},
	// Moved out of `list.campaign.empty` together with the campaign table, which now lives in the
	// step's Campaigns section.
	empty: {
		title: "No campaigns yet",
		description:
			"Add a copywriting campaign to start reaching out, or copy proven campaigns from another list. This step is optional — you can skip it if you're only using this list for enrichment.",
		createNew: "Create new campaign",
		copyFromList: "Copy from other list",
		skipPrompt: "Just enriching this list?",
		skipLink: "Skip to settings",
	},
	// The "Setting up outreach" modal (PREC-3879, mockup v12). Auto-opens on the Outreach step
	// until dismissed for good via the `user_ui_flow` marker.
	explainer: {
		title: "Setting up outreach",
		campaigns: {
			title: "Campaigns",
			subtitle: "One sequence → many leads",
			point1: "Everyone shares a trigger",
			point2: "Write the steps once",
			point3Prefix: "Write it yourself with variables, or drop in",
			point3Term: "AI Snippets",
			point3Tooltip:
				"AI Snippets fill in just a few words or a sentence per lead — you write the core of the message, the snippet personalises the gaps.",
			point4: "Best when the reason is the same for all",
		},
		chat: {
			title: "Chat",
			subtitle: "One message → one signal",
			point1: "Each lead arrives for its own reason",
			point2: "Drafted from that signal, into the chat view",
			point3: "Write each message yourself, or let AI draft it",
			point4: "Best when the reason differs per lead",
		},
		enrichOnly: {
			badge: "Enrich only",
			text: "If you only want to enrich this list, you can skip the Outreach settings",
		},
		chatOrCampaigns: {
			badge: "Chat or campaigns?",
			body: "You can run campaigns and chat on the same list. They never double up — a lead already in a live campaign won't also get a chat draft for the same signal, and chat picks them back up once the campaign ends. Your",
			emphasis: "Copywriting instructions",
			bodyEnd: "feed both.",
		},
		// The event row's body flows around literal variable tokens (`{event_name}`, …) that stay
		// untranslated, hence the split keys.
		eventLists: {
			badge: "Event-based lists",
			body1:
				"Turn this on when the whole list points at one shared moment — a trade show or webinar. It unlocks event variables like",
			body2: "and",
			body3:
				"to drop into any message, and auto-stops every campaign at the event's end so nobody gets invited after it's over. Leave it off when each lead's reason to hear from you is their own.",
		},
		dontShowAgain: "Don't show this again",
		gotIt: "Got it",
	},
};
